import React, { useCallback, useRef, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import FASTPlaylistForm from "./FASTPlaylistForm";
import MediaAPI from "../../../api/MediaAPI";
import {
  AD_BREAK,
  DURATION_FILTER_LIST,
  MEDIA_FORMATS,
} from "../../../constants/media";
import Collapsed from "../../common/tools/Collapsed";
import MediaLibrary from "../mediaLibrary/MediaLibrary";

import type { JSX } from "react";

import type {
  FormatFilter,
  GetFastIndexMediaListResponse,
} from "../../../api/MediaAPI.types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";
import type { MediaLibraryFilterState } from "../mediaLibrary/MediaLibrary";

export default function FASTPlaylistEdit(): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const defaultFormatFilter: FormatFilter = {
    excludes: [MEDIA_FORMATS.ADPOD, AD_BREAK],
  };

  const filterRef = useRef<MediaLibraryFilterState | null>(undefined);
  const [isFilterValid, setIsFilterValid] = useState(false);

  const handleFilterStateChange = useCallback(
    (currentFilter: MediaLibraryFilterState) => {
      filterRef.current = currentFilter;

      if (currentFilter.mediaCount <= 0) {
        setIsFilterValid(false);
      } else if (currentFilter.isFilterActive !== isFilterValid) {
        setIsFilterValid(currentFilter.isFilterActive);
      }
    },
    [isFilterValid],
  );

  const getMediaListMutation = useMutation({
    mutationFn: async () =>
      MediaAPI.getFastIndexMediaList({
        channelId,
        search: filterRef.current?.search,
        filter: filterRef.current?.filter,
        order: filterRef.current?.order,
        perPage: -1,
      }),
    onSuccess: (data) => data.data,
  });
  const handleAddAllFilteredMedia =
    async (): Promise<GetFastIndexMediaListResponse> =>
      getMediaListMutation.mutateAsync();

  const isAddFilteredMediaDisabled =
    !isFilterValid || getMediaListMutation.isLoading;

  return (
    <React.Fragment>
      <div className="mr-[0.78125rem] flex h-full">
        <Collapsed title={t("programs_inventory")}>
          <MediaLibrary
            defaultFormatFilter={defaultFormatFilter}
            durationFilterList={DURATION_FILTER_LIST}
            onFilterStateChange={handleFilterStateChange}
          />
        </Collapsed>
      </div>
      <FASTPlaylistForm
        isAddFilteredMediaDisabled={isAddFilteredMediaDisabled}
        onAddAllFilteredMedia={handleAddAllFilteredMedia}
      />
    </React.Fragment>
  );
}
